import React from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import type { ILead } from 'src/services/api';
import { Flex, FlexColumn } from 'src/components/flex';
import EditFactButton from 'src/components/lead-sidebar/EditFactButton';
import { useJustAddedKeys } from 'src/components/lead-sidebar/just-added-facts-context';
import { EMPTY_VALUE_PLACEHOLDER, getDisplayedFacts } from 'src/components/lead-sidebar/displayed-lead-facts';

import styles from 'src/components/lead-sidebar/LeadFactsList.module.css';
import highlightStyles from 'src/components/lead-sidebar/just-added-highlight.module.css';
import conversationBoxLogo from 'src/assets/img/conversation/box_logo.png';

const AI_ICON_FACT_KEYS = new Set(['annual_income', 'household_size', 'voucher_type']);

function LeadFact({
    field,
    label,
    value,
    isSet = true,
    editButton,
    isJustAdded = false,
}: {
    field: string;
    label: string;
    value: string;
    isSet?: boolean;
    editButton?: React.ReactElement<{ children?: React.ReactNode }>;
    isJustAdded?: boolean;
}) {
    const labelElem = (
        <Typography variant="body2" color="text.secondary" noWrap className={styles.label}>
            {label}:
        </Typography>
    );

    const valueElem = editButton ? (
        <Typography
            variant="body2"
            noWrap
            title={isSet ? value : `Add ${label}`}
            color={isSet ? undefined : 'text.secondary'}
            className={styles.editableValue}
        >
            {isSet ? value : EMPTY_VALUE_PLACEHOLDER}
        </Typography>
    ) : (
        <Typography variant="body2" noWrap title={value}>{value}</Typography>
    );

    return (
        <FlexColumn rowGap={0} className={isJustAdded ? highlightStyles.justAddedHighlight : undefined}>
            <Flex alignItems="center" columnGap={0.5}>
                {labelElem}
                {editButton ? React.cloneElement(editButton, {}, valueElem) : valueElem}
                {isSet && AI_ICON_FACT_KEYS.has(field) && (
                    <Tooltip title="Extracted with AI">
                        <img src={conversationBoxLogo} alt="Extracted with AI" width={10} height={10} />
                    </Tooltip>
                )}
            </Flex>
        </FlexColumn>
    );
}

type Props = {
    lead: ILead;
}

/**
 * Renders the lead's fact columns as a flat list (no surrounding section/accordion).
 * Unset facts render as an "Add" placeholder so they can be filled in manually when
 * nothing was extracted. Used inside the Lead Info section and the lead-facts modal.
 */
export default function LeadFactsList({ lead }: Props) {
    const displayedFacts = getDisplayedFacts(lead);

    const justAddedKeys = useJustAddedKeys();

    return (
        <FlexColumn rowGap={1.5}>
            {displayedFacts.map(({ key, label, value, rawValue, isSet }) => (
                <LeadFact
                    key={key}
                    field={key}
                    label={label}
                    value={value}
                    isSet={isSet}
                    isJustAdded={justAddedKeys.has(key)}
                    editButton={
                        <EditFactButton
                            leadUuid={lead.uuid}
                            title={label}
                            testId={key}
                            factKey={key}
                            value={rawValue}
                        />
                    }
                />
            ))}
        </FlexColumn>
    );
}
